/**
 * Pure mapping from an AI-agent "shot" (one beat of a generated storyboard)
 * to a WaveSpeed `/submit` request.
 *
 * Endpoint selection mirrors `resolveEndpoint` in
 * `src/components/studio/HeroGenerator.tsx`: a reference image steers video
 * to the i2v variant and image to the edit variant; no reference falls back
 * to t2v / t2i. Model ids are verified against `CURATED_VIDEO_BRANDS` /
 * `CURATED_IMAGE_BRANDS` in `src/lib/ai/wavespeed.ts`.
 *
 * No network calls happen here — this only builds the `{ model, params }`
 * pair that a caller sends to `/submit` alongside `shot.prompt`.
 */

export interface GenShot {
  order: number;
  description: string;
  kind: 'video' | 'image';
  prompt: string;
  useReferenceImage?: boolean;
  durationSec?: number;
  aspectRatio?: '9:16' | '16:9' | '1:1';
}

// Verified against src/lib/ai/wavespeed.ts CURATED_VIDEO_BRANDS (Seedance 2.0
// Turbo, lines 388-395) and CURATED_IMAGE_BRANDS (Nano Banana 2, lines
// 267-274) — chosen for the best price/speed/quality balance among curated
// brands.
export const DEFAULT_VIDEO_MODEL = 'bytedance/seedance-2.0/text-to-video-turbo';
export const DEFAULT_VIDEO_MODEL_I2V = 'bytedance/seedance-2.0/image-to-video-turbo';
export const DEFAULT_IMAGE_MODEL = 'google/nano-banana-2/text-to-image';
export const DEFAULT_IMAGE_MODEL_EDIT = 'google/nano-banana-2/edit';

const DEFAULT_ASPECT_RATIO: NonNullable<GenShot['aspectRatio']> = '9:16';
const DEFAULT_DURATION_SEC = 6;
const MIN_DURATION_SEC = 4;
const MAX_DURATION_SEC = 12;

export interface WavespeedRequest {
  model: string;
  params: Record<string, unknown>;
}

function clampDuration(durationSec: number | undefined): number {
  const value = durationSec ?? DEFAULT_DURATION_SEC;
  return Math.min(MAX_DURATION_SEC, Math.max(MIN_DURATION_SEC, value));
}

const MODEL_LABELS: Record<string, string> = {
  [DEFAULT_VIDEO_MODEL]: 'Seedance 2.0 Turbo',
  [DEFAULT_VIDEO_MODEL_I2V]: 'Seedance 2.0 Turbo',
  [DEFAULT_IMAGE_MODEL]: 'Nano Banana 2',
  [DEFAULT_IMAGE_MODEL_EDIT]: 'Nano Banana 2',
};

/** Friendly label for a WaveSpeed model id (falls back to a cleaned id). */
export function wavespeedModelLabel(modelId: string): string {
  if (MODEL_LABELS[modelId]) return MODEL_LABELS[modelId];
  // "vendor/name-slug/variant" → "Name Slug"
  const mid = modelId.split('/')[1] ?? modelId;
  return mid
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Maps a GenShot + optional reference image URL to the exact WaveSpeed
 * `/submit` payload (minus `prompt`, which the caller sends as a sibling
 * field alongside this result). Pure — no I/O.
 */
export function shotToWavespeedRequest(
  shot: GenShot,
  referenceUrl: string | null
): WavespeedRequest {
  const aspectRatio = shot.aspectRatio ?? DEFAULT_ASPECT_RATIO;
  const hasReference = !!referenceUrl && !!shot.useReferenceImage;

  if (shot.kind === 'video') {
    const params: Record<string, unknown> = {
      aspect_ratio: aspectRatio,
      duration: clampDuration(shot.durationSec),
    };
    if (hasReference) {
      params.image = referenceUrl;
      return { model: DEFAULT_VIDEO_MODEL_I2V, params };
    }
    return { model: DEFAULT_VIDEO_MODEL, params };
  }

  // kind === 'image'
  const params: Record<string, unknown> = {
    aspect_ratio: aspectRatio,
  };
  if (hasReference) {
    params.images = [referenceUrl];
    return { model: DEFAULT_IMAGE_MODEL_EDIT, params };
  }
  return { model: DEFAULT_IMAGE_MODEL, params };
}
